/**
 * Scroll Animations
 * =================
 * Global scroll-reveal (IntersectionObserver) and parallax (rAF) system.
 *
 * Usage — reveal:
 *   <div class="section" data-animate>              simple fade
 *   <div class="section" data-animate="fade-up">    fade + slide up
 *   <div class="section" data-animate data-animate-no-stagger>  no child stagger
 *
 * Usage — parallax:
 *   <img data-parallax="-0.3">   counter-scroll at 30 % speed
 *
 * Presets: (bare) | fade-up | fade-down | fade-left | fade-right | scale-up
 */

import { prefersReducedMotion, requestIdleCallback } from '@theme/utilities';

/* ═══════════════════════════════════════════
   SCROLL REVEAL
   ═══════════════════════════════════════════ */

class ScrollReveal {
  /** @type {IntersectionObserver} */
  #observer;

  /** @type {WeakSet<Element>} */
  #revealed = new WeakSet();

  constructor() {
    if (prefersReducedMotion()) {
      this.#revealAll();
      return;
    }

    this.#observer = new IntersectionObserver(this.#onIntersect, {
      threshold: 0,
      rootMargin: '0px 0px -40% 0px',
    });

    this.#scan();
    this.#watchDOM();
  }

  /* ── IntersectionObserver callback ── */

  /** @param {IntersectionObserverEntry[]} entries */
  #onIntersect = (entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;

      const el = entry.target;
      el.classList.add('is-visible');

      if (el.dataset.animateOnce !== 'false') {
        this.#observer.unobserve(el);
        this.#revealed.add(el);
      }
    }
  };

  /* ── Scan for [data-animate] elements ── */

  #scan() {
    for (const el of document.querySelectorAll('[data-animate]')) {
      if (this.#revealed.has(el)) continue;

      // Assign stagger indices unless opted out
      if (!el.hasAttribute('data-animate-no-stagger')) {
        this.#assignStagger(el);
      }

      this.#observer.observe(el);
    }
  }

  /* ── Stagger index assignment ──
     Targets: .container > [layout-div] > [children]
     These are the high-level content blocks (heading, image, body, button,
     grid columns) — NOT deeply nested items. */

  /** @param {Element} parent */
  #assignStagger(parent) {
    const container = parent.querySelector(':scope > .container');
    if (!container) return;

    // The layout div is the first (and usually only) direct child of .container
    const layoutDiv = container.querySelector(':scope > *');
    if (!layoutDiv) return;

    let index = 0;
    for (const child of layoutDiv.children) {
      // Skip elements that have their own data-animate (they're observed independently)
      if (child.hasAttribute('data-animate')) continue;
      child.style.setProperty('--stagger-index', String(index));
      index++;
    }
  }

  /* ── Reduced motion: reveal everything immediately ── */

  #revealAll() {
    requestIdleCallback(() => {
      for (const el of document.querySelectorAll('[data-animate]')) {
        el.classList.add('is-visible');
      }
    });
  }

  /* ── Watch for dynamically added sections (theme editor, section rendering) ── */

  #watchDOM() {
    const target = document.getElementById('MainContent') || document.body;
    const mo = new MutationObserver(() => {
      requestIdleCallback(() => this.#scan());
    });
    mo.observe(target, { childList: true, subtree: true });
  }
}

/* ═══════════════════════════════════════════
   SCROLL PARALLAX
   ═══════════════════════════════════════════ */

class ScrollParallax {
  /** @type {Array<{ el: HTMLElement, speed: number }>} */
  #items = [];

  /** @type {boolean} */
  #ticking = false;

  constructor() {
    if (prefersReducedMotion()) return;

    this.#collect();
    this.#listen();
    this.#update();
  }

  #collect() {
    this.#items = [];
    for (const el of document.querySelectorAll('[data-parallax]')) {
      const speed = parseFloat(/** @type {HTMLElement} */ (el).dataset.parallax) || 0;
      if (speed === 0) continue;
      this.#items.push({ el: /** @type {HTMLElement} */ (el), speed });
    }
  }

  #listen() {
    window.addEventListener(
      'scroll',
      () => {
        if (this.#ticking) return;
        this.#ticking = true;
        requestAnimationFrame(() => {
          this.#update();
          this.#ticking = false;
        });
      },
      { passive: true }
    );

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.#ticking = false;
    });

    // Re-collect when DOM changes
    const target = document.getElementById('MainContent') || document.body;
    const mo = new MutationObserver(() => {
      requestIdleCallback(() => this.#collect());
    });
    mo.observe(target, { childList: true, subtree: true });
  }

  #update() {
    const vh = window.innerHeight;

    for (const { el, speed } of this.#items) {
      const rect = el.getBoundingClientRect();

      // Skip elements far from the viewport
      if (rect.bottom < -vh || rect.top > vh * 2) continue;

      // progress: 0 at viewport center, negative above, positive below
      const center = rect.top + rect.height / 2;
      const progress = (vh / 2 - center) / vh;
      const offset = Math.round(progress * speed * 100);

      el.style.setProperty('--parallax-y', `${offset}px`);
    }
  }
}

/* ═══════════════════════════════════════════
   INIT — on idle so we don't block first paint
   ═══════════════════════════════════════════ */

requestIdleCallback(() => {
  new ScrollReveal();
  new ScrollParallax();
});
